/**
 * Custom UI components for the right sidebar.
 *
 * @see https://www.figma.com/design/hJNJ984SwXCkZCuk7Een9x/BBL---Design-System?node-id=7137-35663&m=dev
 */

import { cn } from "@/lib/utils.ts";
import { HeaderUtilitySlot } from "@/components/layout/HeaderUtilitySlot.tsx";
import { Button } from "@/components/ui/button.tsx";
import {
  SplitPanelHeader,
  panelHeaderControlClass,
  panelHeaderCrumbTitleClass,
  panelTrailingIconClass,
} from "@/components/layout/SplitPanel/SplitPanelHeader.tsx";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { ChevronLeft, X } from "lucide-react";
import { Link } from "wouter";
import { useRightSidebarQueryParamState, useSidebarBack } from "./hooks.tsx";

export const SidebarTextSubtitle = ({
  className,
  ...props
}: React.PropsWithChildren<React.HTMLAttributes<HTMLParagraphElement>>) => {
  return <p className={cn("text-muted-foreground text-xs font-semibold", className)} {...props} />;
};

export const SidebarTextHeading = ({
  className,
  ...props
}: React.PropsWithChildren<React.HTMLAttributes<HTMLParagraphElement>>) => {
  return (
    <p
      className={cn(
        "text-accent-foreground font-semibold decoration-solid text-underline-offset-auto text-underline-position-from-font hover:no-underline",
        className,
      )}
      {...props}
    />
  );
};

export const SidebarTextBody = ({
  className,
  ...props
}: React.PropsWithChildren<React.HTMLAttributes<HTMLDivElement>>) => {
  return <div className={cn("text-base-foreground font-normal", className)} {...props} />;
};

/**
 * @see https://www.figma.com/design/hJNJ984SwXCkZCuk7Een9x/BBL---Design-System?node-id=7210-9612&m=dev
 */
export const RightSidebarHeader = ({
  title,
  titleLink,
  children,
  CTA,
  handleClose,
  handleBack,
  onTitleClick,
  className,
}: {
  title: string;
  titleLink?: string;
  /** Optional controls rendered between the title and the right-side actions (e.g. nav buttons). */
  children?: React.ReactNode;
  CTA?: React.ReactNode;
  handleClose: () => void;
  /**
   * Back navigation. Pass a handler to override; omit to use the generic
   * "back to the parent sidebar" behavior, which renders a back arrow only when
   * this sidebar was opened with `openSidebar(..., { withBack: true })`.
   */
  handleBack?: () => void;
  onTitleClick?: () => void;
  /** Merged onto the header row. For a sidebar whose body sets its own gutter,
   *  so the title sits on the same line as the content beneath it. */
  className?: string;
}) => {
  const { canGoBack, goBack } = useSidebarBack();
  const isOpen = !!useRightSidebarQueryParamState();
  const onBack = handleBack ?? (canGoBack ? goBack : undefined);
  return (
    // The shell every panel header wears, rather than a restatement of it: one
    // height, one gutter, one type scale, one icon size, so a panel an inch away
    // does not read as a different app's chrome.
    //
    // Transparent, because a sidebar is the colour of the surface hosting it.
    <SplitPanelHeader className={cn("bg-transparent", className)}>
      {onBack && (
        <Button
          variant="ghost"
          size="icon"
          onClick={onBack}
          className={cn(panelHeaderControlClass, "-ml-1.5 mr-1 shrink-0")}
          data-statsig-name="sidebar-back"
        >
          <ChevronLeft />
          <span className="sr-only">Back</span>
        </Button>
      )}
      <Tooltip>
        <TooltipTrigger asChild>
          {titleLink ? (
            <Link href={titleLink} className={cn(panelHeaderCrumbTitleClass, "flex-1")}>
              {title}
            </Link>
          ) : (
            <h2
              className={cn(panelHeaderCrumbTitleClass, "flex-1", onTitleClick && "cursor-pointer")}
              onClick={onTitleClick}
            >
              {title}
            </h2>
          )}
        </TooltipTrigger>
        <TooltipContent>{title}</TooltipContent>
      </Tooltip>
      {children}
      {CTA}
      {/* One right-aligned group rather than a lone close pinned by `ml-auto`,
          so the utilities can sit beside it at the row's own spacing. Close
          keeps the corner: it is how you leave this panel, and a control that
          moved off the edge when a dev build added a button beside it would be
          the wrong thing to give up. */}
      <div className="ml-auto flex shrink-0 items-center gap-0.5">
        {/* Only while the sidebar is actually open. Its content stays mounted
            through the close animation, and a sidebar sliding off screen still
            outranks the page header — so without this the controls ride it out
            of view and reappear when it finally unmounts. */}
        {isOpen && <HeaderUtilitySlot layer="sidebar" />}
        <Button
          variant="ghost"
          size="icon"
          onClick={handleClose}
          className={cn(panelHeaderControlClass, panelTrailingIconClass)}
          data-statsig-name="sidebar-close"
        >
          <X />
          <span className="sr-only">Close</span>
        </Button>
      </div>
    </SplitPanelHeader>
  );
};
